import React, { useEffect, useState, useRef } from "react";
import { PropTypes } from "prop-types";
import {
  Table,
  Button,
  Input,
  Tooltip,
  Dropdown,
  Empty,
  Checkbox,
  ConfigProvider,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  CaretDownOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import HorizontalSpacer from "../layouts/HorizontalSpacer";
import ActionsTableWrapper from "./ActionsTable.style";
import { useDispatch, useSelector } from "react-redux";
import RestrictedComponent from "../util/RestrictedComponent";

var flatten = require("flat");

const ActionsTable = (props) => {
  const {
    tableItems,
    ltrItems,
    category,
    current,
    pageSize,
    total,
    loading,
    textFilter,
    reload,
    resetPage,
    loadPage,
    items,
    order,
    filters,
    filtersType,
    paramsFilters,
    onTextChange,
    onAddClick,
    showheader,
    headerInputPlaceholder,
    addbuttonText,
    renderActions,
    borderless,
    expandableStyle,
    expandable,
    inputStyle,
    hideHeaderInput,
    hideRefresh,
    emptyImage,
    emptytext,
    resource,
    dropDownMenu,
    onCheckboxChange,
    checkBoxLabel,
    showInactive,
    checkActive = (rec) => rec.active,
    noTabs,
    deep,
    rowClassName,
    skipInitialLoad,
    key,
    filtersDataIndex,
    autoFocus,
    scroll,
    onTableChange,
    subHeaderComponent,
  } = props;
  const dispatch = useDispatch();
  const [flattenList, setFlattenList] = useState([]);
  const [columns, setColumns] = useState([]);
  const [shouldShowEmpty, setShouldShowEmpty] = useState(false);

  let { branchId } = useParams();
  const direction = useSelector(
    (state) => state.user.currentAppLocale.direction
  );
  const headerInputRef = useRef();

  useEffect(() => {
    let timeoutId;

    if (!loading && !shouldShowEmpty) {
      timeoutId = setTimeout(() => {
        setShouldShowEmpty(true);
      }, 500);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [loading]);

  useEffect(() => {
    let arr = tableItems;
    let tableColumns = [];
    arr.forEach(function (item) {
      const title = "app." + category + "." + item.title;
      const i = item.render
        ? {
            title: item.showOriginTitle ? (
              item.title
            ) : (
              <FormattedMessage id={title} />
            ),
            dataIndex: item.sortIndex ? item.sortIndex : item.title,
            sortOrder:
              order && item.sortIndex === order.field ? order.order : null,
            responsive: item.responsive,
            width: item.width ? item.width : "auto",
            filters: item.filters,
            sorter: item.sorter,
            ellipsis: item.ellipsis,
            fixed: item.fixed,
            render(text, record) {
              const Comp = item.render;
              return (
                <Comp
                  category={props.category}
                  record={record}
                  text={text}
                  ellipsis={item.ellipsis}
                  {...item.columnProps}
                />
              );
            },
          }
        : {
            title: item.showOriginTitle ? (
              item.title
            ) : (
              <FormattedMessage id={title} />
            ),
            dataIndex: item.title,
            key: item.sortIndex ? item.sortIndex : item.title,
            responsive: item.responsive,
            filters: item.filters,
            sortOrder:
              order && item.sortIndex === order.field ? order.order : null,
            sorter: item.sorter,
            width: item.width ? item.width : "auto",
            ellipsis: {
              showTitle: item.ellipsis ? false : true,
            },
            fixed: item.fixed,
            onCell: () => ({
              style: {
                direction:
                  ltrItems && ltrItems.includes(item.title) ? "ltr" : "inherit",
              },
            }),
            render(text, record) {
              return item.ellipsis ? (
                <Tooltip className="content" placement="topLeft" title={text}>
                  {text}
                </Tooltip>
              ) : (
                <div className="content">{text}</div>
              );
            },
          };
      if (filters) {
        i["filteredValue"] = filters[item.title] || null;
      }

      tableColumns.push(i);
    });
    setColumns(tableColumns);
    if (autoFocus && headerInputRef && headerInputRef.current) {
      const { input } = headerInputRef.current;
      input.focus();
    }
  }, [category, ltrItems, props.category, tableItems]);

  useEffect(() => {
    if (reload && !skipInitialLoad) {
      dispatch(reload());
    }
    return function cleanup() {
      if (resetPage) {
        dispatch(resetPage());
      }
    };
  }, [dispatch, branchId, reload]);

  useEffect(() => {
    let flatList = [];
    items.forEach((item) => {
      const flattenItem = flatten(item, { safe: true });
      flatList.push(flattenItem);
    });
    if (!deep) {
      setFlattenList(flatList);
    }
  }, [items]);

  const onChange = (
    { current, pageSize },
    filters,
    { field, order },
    extra
  ) => {
    if (onTableChange) {
      onTableChange({ current, pageSize }, filters, { field, order }, extra);
    }
    let finalfilters = filters;
    if (filtersDataIndex) {
      const changedKeys = Object.keys(filtersDataIndex);
      finalfilters = changedKeys.reduce((previous, item) => {
        if (filters[item] === undefined) {
          return previous;
        }
        const key = filtersDataIndex[item] || item;
        previous[key] = filters[item];
        return previous;
      }, {});
    }
    let keys = Object.keys(finalfilters);
    let extraParams = {};
    if (paramsFilters && finalfilters) {
      keys = keys.filter((i) => !paramsFilters.includes(i));
      paramsFilters.forEach((item, i) => {
        extraParams[item] = finalfilters[item];
      });
    }

    let filter = {};
    if (finalfilters) {
      keys.forEach((key, i) => {
        const temp = [];
        if (finalfilters[key]) {
          finalfilters[key].forEach((item, i) => {
            const type = filtersType[key];
            temp.push({
              [key]:
                type === "number"
                  ? parseInt(item)
                  : type === "boolean"
                  ? item === true
                  : item,
            });
          });
          if (filter["$and"]) {
            filter["$and"].push({ $or: temp });
          } else {
            filter["$and"] = [{ $or: temp }];
          }
        }
      });
    }
    const pageNumber = extra["action"] === "paginate" ? current : 1;
    filter["freeText"] = textFilter;
    const page = {
      current: pageNumber,
      pageSize,
      filter,
      order: {
        field,
        order,
      },
      finalfilters,
    };

    if (showInactive) {
      extraParams = { ...extraParams, include_not_active: showInactive };
    }
    dispatch(loadPage(page, { ...extraParams }));
  };

  const pagination = {
    current: current,
    pageSize: pageSize,
    total: total,
    // simple: true,
    // showTitle: true,
    // showQuickJumper: true,
    pageSizeOptions: [10, 50, 100],
  };

  const AddBtn = () =>
    dropDownMenu ? (
      <Dropdown.Button
        icon={<CaretDownOutlined />}
        size="large"
        style={{ margin: hideRefresh ? "0px" : "0 10px" }}
        type="primary"
        onClick={onAddClick}
        menu={dropDownMenu}
      >
        <PlusOutlined /> {addbuttonText}
      </Dropdown.Button>
    ) : (
      <Button
        style={{ margin: hideRefresh ? "0px" : "0 10px" }}
        type="primary"
        onClick={onAddClick}
        size="large"
      >
        <PlusOutlined /> {addbuttonText}
      </Button>
    );

  const borderStyle = borderless ? "borderless" : "tableWrapper";
  const expandStyle = expandableStyle ? "expandable" : "";
  const className = `${borderStyle} ${expandStyle}`;

  return (
    <ActionsTableWrapper className={className}>
      {showheader ? (
        <HorizontalSpacer style={{ paddingBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {hideHeaderInput ? (
              <div />
            ) : (
              <div>
                <Input
                  prefix={
                    <SearchOutlined
                      style={{ padding: "0  5px", opacity: 0.4 }}
                    />
                  }
                  placeholder={headerInputPlaceholder}
                  value={textFilter}
                  onChange={onTextChange}
                  style={{ width: inputStyle ? inputStyle : 300 }}
                  size="large"
                  allowClear
                  ref={headerInputRef}
                />
              </div>
            )}
            {subHeaderComponent ? subHeaderComponent : null}
            {/* Render the SubHeader component */}
            {onCheckboxChange ? (
              <Checkbox
                style={{ margin: "0 20px" }}
                onChange={onCheckboxChange}
                checked={showInactive}
              >
                {checkBoxLabel}
              </Checkbox>
            ) : null}
          </div>
          <div style={{ display: "flex" }}>
            {onAddClick ? (
              process.env.REACT_APP_BUILD_TARGET !== "tenants" ? (
                <RestrictedComponent
                  resource={resource}
                  action={"editor"}
                  comp={resource ? null : <AddBtn />}
                >
                  <AddBtn />
                </RestrictedComponent>
              ) : (
                <AddBtn />
              )
            ) : null}
            {renderActions ? renderActions : null}

            {hideRefresh ? null : (
              <Button
                type="text"
                onClick={() => dispatch(reload())}
                size="large"
                shape="circle"
                icon={
                  <ReloadOutlined
                    className={direction === "rtl" ? "reloadRtl" : "reload"}
                  />
                }
              />
            )}
          </div>
        </HorizontalSpacer>
      ) : null}
      <ConfigProvider
        renderEmpty={() =>
          shouldShowEmpty === true ? (
            <Empty
              image={emptyImage}
              description={
                emptytext ? (
                  emptytext
                ) : (
                  <FormattedMessage id={"app.tables.noData"} />
                )
              }
            />
          ) : (
            <div style={{ height: "150px" }} />
          )
        }
      >
        <Table
          key={key}
          loading={loading}
          rowClassName={
            rowClassName
              ? rowClassName
              : (record, index) =>
                  checkActive(record) === false ? "disabled" : ""
          }
          rowKey={(record) => record.id}
          columns={columns}
          dataSource={deep ? items : flattenList}
          onChange={onChange}
          pagination={pagination}
          expandable={expandable}
          scroll={{
            y: scroll
              ? scroll
              : noTabs
              ? window.innerHeight - 426
              : window.innerHeight - 470,
          }}
          bordered
        />
      </ConfigProvider>
    </ActionsTableWrapper>
  );
};

ActionsTable.propTypes = {
  tableItems: PropTypes.array,
  ltrItems: PropTypes.array,
  category: PropTypes.string,
  actions: PropTypes.bool,
  current: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  loading: PropTypes.bool,
  textFilter: PropTypes.string,
  reload: PropTypes.func,
  loadPage: PropTypes.func,
  items: PropTypes.array,
  onTextChange: PropTypes.func,
  onAddClick: PropTypes.func,
  addbuttonText: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  showheader: PropTypes.bool,
  headerInputPlaceholder: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  borderless: PropTypes.bool,
  inputStyle: PropTypes.number,
  hideRefresh: PropTypes.bool,
  resource: PropTypes.string,
  dropDownMenu: PropTypes.object,
  onCheckboxChange: PropTypes.func,
  showInactive: PropTypes.bool,
  emptyImage: PropTypes.object,
  emptytext: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  checkBoxLabel: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  subHeaderComponent: PropTypes.node, // Add propType for the new component
};

export default ActionsTable;
